import React, { useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import Grid from '@mui/material/Grid'
import * as Yup from 'yup'
import {
  DeleteOutline,
  EditOutlined,
  SaveOutlined,
  CancelOutlined,
} from '@mui/icons-material'
import Button from '@/v2/components/common/Button'
import Typography from '@/v2/components/common/Typography'
import TextField from '@/v2/components/common/TextField'
import { generateEmptyProcedure } from '../../utils'
import Box from '@mui/material/Box'
import {
  useAddClaimItem,
  useUpdateClaimItem,
  useRemoveClaimItem,
} from '@/v2/hooks/insurance/claim-subforms/useClaimItems'
import SelectCalendar from '@/v2/components/common/SelectCalendar'
import Divider from '@/v2/components/common/Divider'

const procedureSchema = Yup.object().shape({
  procedureCode: Yup.string().required('Code is required'),
  fee: Yup.number()
    .transform(value => (value === '' ? null : value))
    .required('Fee is required')
    .positive('Fee must be positive'),
  quantity: Yup.number()
    .transform(value => (value === '' ? null : value))
    .required('Quantity is required')
    .positive('Quantity must be positive'),
  startDate: Yup.date()
    .required('Start date is required')
    .test(
      'not-future',
      'Start date cannot be in the future',
      value => !value || value <= new Date()
    ),
})

const Procedures = ({
  values = {},
  onChange,
  onItemSaved,
  claimId,
  viewOnly = false,
}) => {
  const theme = useTheme()
  const [procedures, setProcedures] = useState(values?.procedure || [])
  const [editingProcedureId, setEditingProcedureId] = useState(null)
  const [isAdding, setIsAdding] = useState(false)
  const [initialProcedureData, setInitialProcedureData] = useState(
    generateEmptyProcedure()
  )
  const [validationErrors, setValidationErrors] = useState({})

  const { addClaimItem } = useAddClaimItem(claimId)
  const { updateClaimItem } = useUpdateClaimItem(claimId, editingProcedureId)
  const { removeClaimItem } = useRemoveClaimItem(claimId)

  useEffect(() => {
    setProcedures(values?.items || [])
  }, [values.items])

  const updateProcedure = (procedureId, updatedValue) => {
    const updatedProcedures = procedures.map(item =>
      item.id === procedureId ? { ...item, ...updatedValue } : item
    )
    setProcedures(updatedProcedures)
    if (onChange) onChange({ ...values, procedure: updatedProcedures })
  }

  const handleDeleteProcedure = async procedureId => {
    const res = await removeClaimItem(procedureId)
    if (!res.error) {
      const updatedProcedures = procedures.filter(
        ({ id }) => id !== procedureId
      )
      setProcedures(updatedProcedures)
      if (onChange) onChange({ ...values, procedure: updatedProcedures })
    }
  }

  const handleAddProcedure = () => {
    setIsAdding(true)
    setEditingProcedureId(null)
    setInitialProcedureData(generateEmptyProcedure())
    setValidationErrors({})
  }

  const handleSaveProcedure = async () => {
    try {
      // Validate
      await procedureSchema.validate(initialProcedureData, {
        abortEarly: false,
      })
      setValidationErrors({})

      const procedureWithId = initialProcedureData.id
        ? initialProcedureData
        : { ...initialProcedureData, id: Date.now() }

      // Add new
      if (isAdding) {
        const body = {
          item: {
            quantity: procedureWithId.quantity || '1',
            fee: procedureWithId.fee,
            procedureCode: procedureWithId.procedureCode,
            startDate: procedureWithId.startDate,
            endDate: procedureWithId.endDate,
          },
        }

        const res = await addClaimItem(body)
        if (!res.error && res.id) {
          const newProcedureWithRealId = { ...procedureWithId, id: res.id }
          const newProcedures = [...procedures, newProcedureWithRealId]
          setProcedures(newProcedures)
          onChange?.({ ...values, procedure: newProcedures })
          onItemSaved?.(procedureWithId.startDate)
        }
      }
      // Edit existing
      else if (editingProcedureId) {
        const body = {
          item: {
            quantity: procedureWithId.quantity || '1',
            fee: procedureWithId.fee,
            procedureCode: procedureWithId.procedureCode,
            startDate: procedureWithId.startDate,
            endDate: procedureWithId.endDate,
          },
        }

        const res = await updateClaimItem(body)
        if (!res.error) {
          const updatedProcedures = procedures.map(procedure =>
            procedure.id === editingProcedureId ? procedureWithId : procedure
          )
          setProcedures(updatedProcedures)
          onChange?.({ ...values, procedure: updatedProcedures })
          onItemSaved?.(procedureWithId.startDate)
        }
      }

      // Reset
      setIsAdding(false)
      setEditingProcedureId(null)
      setInitialProcedureData(generateEmptyProcedure())
    } catch (validationErr) {
      const errors = {}
      validationErr.inner.forEach(err => {
        errors[
          `${isAdding ? 'newProcedure' : editingProcedureId}.${err.path}`
        ] = err.message
      })
      setValidationErrors(errors)
    }
  }

  const handleEditProcedure = procedureId => {
    setEditingProcedureId(procedureId)
    setIsAdding(false)
    const procedureToEdit = procedures.find(
      procedure => procedure.id === procedureId
    )
    setInitialProcedureData(procedureToEdit)
    setValidationErrors({})
  }

  const handleCancelEdit = () => {
    setEditingProcedureId(null)
    setIsAdding(false)
    setInitialProcedureData(generateEmptyProcedure())
    setValidationErrors({})
  }

  return (
    <>
      <Grid item xs={12} sx={{ marginTop: '12px' }}>
        <Typography variant="h5" bold fontSize="16px">
          Items
        </Typography>
        <Divider margin="5px 0px" />
      </Grid>

      {/* Existing Items */}
      {procedures.map((procedure, index) => {
        const isEditingThis = editingProcedureId === procedure.id
        const currentValues = isEditingThis ? initialProcedureData : procedure
        const errorAffixe = isEditingThis
          ? editingProcedureId
          : `procedure[${index}]`

        return (
          <Grid item key={procedure.id} xs={12}>
            <Grid container spacing={2}>
              {/* Fields row */}
              <Grid item xs={12}>
                <Procedure
                  theme={theme}
                  isEdit={isEditingThis}
                  showLabel={index === 0}
                  values={currentValues}
                  errorAffixe={errorAffixe}
                  error={validationErrors}
                  onChange={value => setInitialProcedureData(value)}
                />
              </Grid>

              {/* Buttons row */}
              {!viewOnly && (
                <Grid item xs={12}>
                  <Box display="flex" flexDirection="row" gap="8px">
                    {isEditingThis ? (
                      <>
                        <Button
                          variant="text"
                          disableElevation
                          startIcon={<SaveOutlined color="link" />}
                          onClick={handleSaveProcedure}
                          sx={{
                            marginBottom: '4px',
                            color: '#000',
                            '&:hover': {
                              backgroundColor: theme.palette.silver.dark,
                            },
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          variant="text"
                          disableElevation
                          startIcon={<CancelOutlined color="error" />}
                          onClick={handleCancelEdit}
                          sx={{
                            marginBottom: '4px',
                            color: '#000',
                            '&:hover': {
                              backgroundColor: theme.palette.silver.dark,
                            },
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="text"
                          disableElevation
                          startIcon={<EditOutlined color="link" />}
                          onClick={() => handleEditProcedure(procedure.id)}
                          sx={{
                            marginBottom: '4px',
                            color: '#000',
                            '&:hover': {
                              backgroundColor: theme.palette.silver.dark,
                            },
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="text"
                          disableElevation
                          startIcon={<DeleteOutline color="link" />}
                          onClick={() => handleDeleteProcedure(procedure.id)}
                          sx={{
                            marginBottom: '4px',
                            color: '#000',
                            '&:hover': {
                              backgroundColor: theme.palette.silver.dark,
                            },
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
        )
      })}

      {/* "Add an item" Button */}
      {!viewOnly && (
        <Grid item xs={12} sx={{ marginTop: '12px', pl: '0 !important' }}>
          {!isAdding && editingProcedureId === null && (
            <Button
              fullWidth
              variant="contained"
              sx={{ backgroundColor: '#F5F5F5' }}
              onClick={handleAddProcedure}
            >
              <Typography
                variant="label"
                sx={{ color: '#000', height: '20px', marginTop: '6px' }}
              >
                + Add an item
              </Typography>
            </Button>
          )}
        </Grid>
      )}

      {/* New procedure form */}
      {isAdding && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Procedure
                theme={theme}
                values={initialProcedureData}
                errorAffixe="newProcedure"
                error={validationErrors}
                isEdit={true}
                isAdding={true}
                onChange={newProcedure => setInitialProcedureData(newProcedure)}
              />
            </Grid>
            {!viewOnly && (
              <Grid item xs={12}>
                <Box display="flex" flexDirection="row" gap="8px">
                  <Button
                    variant="text"
                    disableElevation
                    startIcon={<SaveOutlined color="link" />}
                    onClick={handleSaveProcedure}
                    sx={{
                      marginBottom: '4px',
                      color: '#000',
                      '&:hover': {
                        backgroundColor: theme.palette.silver.dark,
                      },
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="text"
                    disableElevation
                    startIcon={<CancelOutlined color="error" />}
                    onClick={handleCancelEdit}
                    sx={{
                      marginBottom: '4px',
                      color: '#000',
                      '&:hover': {
                        backgroundColor: theme.palette.silver.dark,
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </>
  )
}

const Procedure = ({
  isEdit = false,
  showLabel = true,
  values = {},
  error = {},
  errorAffixe,
  onChange,
  theme,
}) => (
  <Grid container spacing={2}>
    <Grid item xs={6} sm={6} md={2}>
      <SelectCalendar
        label="Start date"
        labelFormat="MM/dd/yyyy"
        isBasicCalendar
        value={values.startDate}
        error={error[`${errorAffixe}.startDate`]}
        onChange={value => onChange({ ...values, startDate: value })}
        disabled={!isEdit}
        outlineColor={
          isEdit ? theme.palette.secondary.main : theme.palette.primary.main
        }
      />
    </Grid>
    <Grid item xs={6} sm={6} md={2}>
      <SelectCalendar
        label="End date"
        labelFormat="MM/dd/yyyy"
        isBasicCalendar
        value={values.endDate}
        error={error[`${errorAffixe}.endDate`]}
        onChange={value => onChange({ ...values, endDate: value })}
        disabled={!isEdit}
        outlineColor={
          isEdit ? theme.palette.secondary.main : theme.palette.primary.main
        }
      />
    </Grid>
    <Grid item xs={6} sm={6} md={3}>
      <TextField
        fullWidth
        name="procedureCode"
        label="Code"
        placeholder="Procedure Code"
        value={values.procedureCode || ''}
        error={error[`${errorAffixe}.procedureCode`]}
        disabled={!isEdit}
        onChange={value => onChange({ ...values, procedureCode: value })}
        outlineColor={
          isEdit ? theme.palette.secondary.main : theme.palette.primary.main
        }
      />
    </Grid>
    <Grid item xs={6} sm={6} md={3}>
      <TextField
        fullWidth
        name="fee"
        label="Fee"
        placeholder="Procedure Fee"
        prefix="$"
        value={values.fee || ''}
        error={error[`${errorAffixe}.fee`]}
        disabled={!isEdit}
        onChange={value =>
          onChange({ ...values, fee: value === '' ? null : Number(value) })
        }
        outlineColor={
          isEdit ? theme.palette.secondary.main : theme.palette.primary.main
        }
      />
    </Grid>
    <Grid item xs={6} sm={6} md={2}>
      <TextField
        fullWidth
        name="quantity"
        label="Quantity"
        placeholder="Quantity"
        value={values.quantity || ''}
        error={error[`${errorAffixe}.quantity`]}
        disabled={!isEdit}
        onChange={value =>
          onChange({ ...values, quantity: value === '' ? null : Number(value) })
        }
        outlineColor={
          isEdit ? theme.palette.secondary.main : theme.palette.primary.main
        }
      />
    </Grid>
  </Grid>
)

export default Procedures
